/**
 * Hook to calculate split point for past/future predictions
 *
 * Calculates:
 * - lastRealEstimate: The last "real" estimate (non-zero, <= current month)
 * - predictionSplitDay: The day to split past/future predictions
 *
 * This is used to ensure future predictions start from the last real estimate value.
 */

import { useMemo } from 'react';
import type { UnifiedForecastResult, RebatePoint } from '@erp/shared-types';

/**
 * Calculate the last "real" estimate and split point
 *
 * @param forecastData - The forecast data (should be merged data)
 * @param currentDate - The current date to use for calculations (defaults to now)
 * @returns Object with lastRealEstimate and predictionSplitDay
 */
export function useSplitPoint(
  forecastData: UnifiedForecastResult | null | undefined,
  currentDate?: Date,
): {
  lastRealEstimate: RebatePoint | null;
  predictionSplitDay: number;
} {
  return useMemo(() => {
    if (!forecastData?.estimates || forecastData.estimates.length === 0) {
      return { lastRealEstimate: null, predictionSplitDay: 0 };
    }

    // Use provided current date or default to now
    const now = currentDate || new Date();
    const currentYear = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth(); // 0-indexed (0 = January, 11 = December)

    // Calculate period start (used throughout)
    const periodStart =
      forecastData.forecastPeriod?.startDate ||
      (forecastData.estimates && forecastData.estimates.length > 0
        ? forecastData.estimates[0].date
        : new Date(currentYear, 0, 1));

    // Get the last day of the current month
    const currentMonthEndDate = new Date(
      Date.UTC(currentYear, currentMonth + 1, 0, 23, 59, 59, 999),
    );

    // Calculate daysFromStart for current month end
    const currentMonthEndDay = Math.ceil(
      (currentMonthEndDate.getTime() - periodStart.getTime()) /
        (1000 * 60 * 60 * 24),
    );

    // Check if we have estimates for the current month
    const hasCurrentMonthEstimates = forecastData.estimates.some((e) => {
      const estimateYear = e.date.getUTCFullYear();
      const estimateMonth = e.date.getUTCMonth();
      return (
        estimateYear === currentYear &&
        estimateMonth === currentMonth &&
        Math.abs(e.totalRebateAmount) >= 0.01
      );
    });

    // Find the last "real" estimate: non-zero value that is <= current month end
    // Backend provides estimates for all months, but only those up to current month
    // with non-zero values are considered "real"
    const lastReal = [...forecastData.estimates]
      .filter((e) => {
        // Must be non-zero
        if (Math.abs(e.totalRebateAmount) < 0.01) return false;
        // Must be at or before current month end
        return Number(e.daysFromStart) <= currentMonthEndDay;
      })
      .sort((a, b) => a.daysFromStart - b.daysFromStart)
      .pop(); // Get the last one

    // Determine the split point:
    // - If we have current month estimates, split at current month end
    // - Otherwise, split at previous month end (not at last estimate date)
    // - NEVER exceed today's date (can't show "future" predictions for past dates)
    let splitDay = 0;
    if (hasCurrentMonthEstimates) {
      splitDay = currentMonthEndDay;
    } else if (currentMonth > 0) {
      // Previous month end (month before current month)
      const previousMonthEndDate = new Date(
        Date.UTC(currentYear, currentMonth, 0, 23, 59, 59, 999),
      );
      splitDay = Math.ceil(
        (previousMonthEndDate.getTime() - periodStart.getTime()) /
          (1000 * 60 * 60 * 24),
      );
    } else if (lastReal) {
      // Fallback: if we're in January and no estimates, use last estimate date
      splitDay = lastReal.daysFromStart;
    }

    // Ensure splitDay is at least today - can't show "future" for dates that have already passed
    // This raises the split point to include all past dates as "past" (black)
    const todayAsDaysFromStart = Math.ceil(
      (now.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24),
    );
    splitDay = Math.max(splitDay, todayAsDaysFromStart);

    return {
      lastRealEstimate: lastReal || null,
      predictionSplitDay: splitDay,
    };
  }, [forecastData, currentDate]);
}
